/* eslint-disable max-len */
import React from 'react';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Col,
  Divider,
  FlexboxGrid,
  Form,
  FormInstance,
  Heading,
  HeadingGroup,
  IconButton,
  Input,
  InputGroup,
  Loader,
  Modal,
  Panel,
  Radio,
  RadioGroup,
  Schema,
  Stack,
  Steps,
  Text,
  toaster,
} from 'rsuite';
import ReloadIcon from '@rsuite/icons/Reload';
import { constant, util } from '@helper';
import { ResumeTemplate } from '@common/components';
import { pdf, PDFViewer } from '@react-pdf/renderer';
import _ from 'lodash';
import moment from 'moment';
import {
  CertificationData,
  LanguageData,
  ProjectData,
  VolunteerData,
} from '@common/types';

import AuthStorage from '@common/services/authStorage';
import {
  FaArrowLeft,
  FaClock,
  FaCloudDownloadAlt,
  FaCopy,
  FaGift,
  FaIdCard,
  FaShareAlt,
} from 'react-icons/fa';
import { Icon } from '@rsuite/icons';
import {
  DocumentAPI,
  EventTrackerAPI,
  InvoiceAPI,
  MiscAPI,
  OrderAPI,
} from '@services/api';
import { Promise } from 'bluebird';
import SessionStorage from '@common/services/sessionStorage';

type Payload = {
  packageId: string | number;
  paymentMethod: string;
  email: string;
  coupon?: string;
};

type State = {
  cvName?: string;
  doc: any;
  downloadableFilename?: string;
  downloadableDoc: any;
  paymentMethods: Array<any>;
  defaultPromoCode?: string;
  step: number;
  showModal: boolean;
  showSuccessModal: boolean;
  showFailedModal: boolean;
  otherEmail?: string;
  previousScreen: string;
  payload: Payload;
  price?: number;
  payButtonEnabled: boolean;
  discounted?: number;
  discountedPrice?: number;
  loadingRefresh?: boolean;
  loading?: boolean;

  invoiceNumber?: string;
  adminFee: number;
  vaNumber?: string;
  billerCode?: string;
  qrUrl?: string;
  expiresIn?: number;
};

const { withRouter } = util;
const { StringType } = Schema.Types;
const PAYMENT_FORM_SECTION_CLASS =
  'max-md:!flex-col max-md:!items-stretch max-md:gap-4 max-md:[&>.rs-stack-item]:!w-full max-md:[&>.rs-stack-item]:min-w-0';

class CVMakerPaymentScreen extends React.Component<any, State> {
  formPaymentRef: React.Ref<
    FormInstance<
      Record<string, any>,
      string,
      {
        [x: string]: string | undefined;
      }
    >
  >;

  otherEmailRadioRef: React.Ref<
    FormInstance<
      Record<string, any>,
      string,
      {
        [x: string]: string | undefined;
      }
    >
  >;

  formPaymentModel: any;

  debouncedApplyCode: any;

  constructor(props: any) {
    super(props);

    this.state = {
      cvName: 'my-resume.pdf',
      doc: null,
      downloadableDoc: null,
      paymentMethods: [],
      step: 1,
      showModal: false,
      showSuccessModal: false,
      showFailedModal: false,
      payButtonEnabled: true,
      previousScreen: '',
      adminFee: 0,
      loading: false,
      payload: {
        packageId: 0,
        paymentMethod: '',
        email: '',
      },
    };

    this.formPaymentRef = React.createRef();
    this.otherEmailRadioRef = React.createRef();
    this.formPaymentModel = Schema.Model({
      email: StringType()
        .isEmail('Invalid email address format!')
        .isRequired('Please fill the required field!'),
      paymentMethod: StringType().isRequired('Please fill the required field!'),
      coupon: StringType(),
    });
    this.debouncedApplyCode = _.debounce(this.applyCode.bind(this), 500);
  }

  async componentDidMount(): Promise<void> {
    // eslint-disable-next-line global-require
    require('./styles.scss');

    const { props } = this;
    const { location, t } = props;

    this.setState({
      doc: (
        <ResumeTemplate.Onyx
          isPreview
          data={{
            name: _.startCase(
              _.get(
                props,
                'location.state.payload.personalData.fullName',
                'Your Name',
              ),
            ),
            tagline: _.startCase(
              _.get(
                props,
                'location.state.payload.personalData.jobTitle',
                'Your current role',
              ),
            ),
            contacts: [
              {
                text: `${_.get(
                  props,
                  'location.state.payload.personalData.city',
                  'Your city',
                )}, ${_.get(
                  props,
                  'location.state.payload.personalData.country',
                  'Your country',
                )}`,
              },
              {
                text: _.get(
                  props,
                  'location.state.payload.personalData.email',
                  'your_email@gmail.com',
                ),
                href: `mailto:${_.get(
                  props,
                  'location.state.payload.personalData.email',
                  'your_email@gmail.com',
                )}`,
              },
              {
                text: `${_.get(
                  props,
                  'location.state.payload.personalData.phoneRegion',
                  '+62',
                )} ${_.get(
                  props,
                  'location.state.payload.personalData.phoneNumber',
                  '811-1111-1111',
                )}`,
              },
              {
                text: String(
                  _.get(
                    props,
                    'location.state.payload.personalData.linkedInProfile',
                    'linkedin.com/in/john-doe',
                  ),
                )
                  .replace(/^https?:\/\//, '')
                  .replace(/^www\./, ''),
                href: _.get(
                  props,
                  'location.state.payload.personalData.linkedInProfile',
                  'https://linkedin.com/in/john-doe',
                ),
              },
            ],
            summary:
              util.parseHtmlBulletAsString(
                _.get(
                  props,
                  'location.state.payload.professionalData.summary',
                ) || '',
              ) || t(constant.translation.cvMaker.formSummaryDefault),
            experience:
              _.get(props, 'location.state.payload.experienceData.data', [])
                .length > 0
                ? _.map(
                    _.get(
                      props,
                      'location.state.payload.experienceData.data',
                      [],
                    ),
                    (d) => ({
                      company: _.startCase(
                        _.get(d, 'employer', 'Your company'),
                      ),
                      role: _.startCase(_.get(d, 'jobTitle', 'Your role')),
                      start: moment(
                        _.get(d, 'startDate', '01/08/2015'),
                        'DD/MM/YYYY',
                      ).format('MMM YYYY'),
                      end:
                        _.get(d, 'endDate') &&
                        moment().format('DD/MM/YYYY') !== _.get(d, 'endDate')
                          ? moment(_.get(d, 'endDate'), 'DD/MM/YYYY').format(
                              'MMM YYYY',
                            )
                          : 'Now',
                      jobType: _.get(d, 'jobType', 'Full Time'),
                      location: _.get(d, 'location', 'Unknown Location'),
                      bullets: util.parseHtmlBullet(
                        _.get(d, 'jobDesc') ||
                          t(constant.translation.cvMaker.formLoremIpsumShorter),
                      ),
                    }),
                  )
                : [
                    {
                      company: 'Your company',
                      role: 'Your role',
                      start: 'Jan 1997',
                      end: 'Now',
                      jobType: 'Full Time',
                      location: 'Indonesia',
                      bullets: [
                        t(constant.translation.cvMaker.formLoremIpsumShorter),
                        t(constant.translation.cvMaker.formLoremIpsumShorter),
                      ],
                    },
                  ],
            projects: _.map(
              _.get(
                props,
                'location.state.payload.extraSectionData.projects',
              ) || [],
              (it: ProjectData) => ({
                name: it.name || '[Project Name]',
                role: it.role || '[Your Role]',
                year: it.year || '[Year]',
                blurb: it.contribution || '[Contribution or Impact]',
              }),
            ),
            certifications: _.map(
              _.get(
                props,
                'location.state.payload.extraSectionData.certifications',
              ) || [],
              (it: CertificationData) => ({
                title: it.name || '[Certificate Name]',
                org: it.issuer || '[Issuer]',
                year: it.year || '[Year]',
              }),
            ),
            volunteers: _.map(
              _.get(
                props,
                'location.state.payload.extraSectionData.volunteers',
              ) || [],
              (it: VolunteerData) => ({
                name: it.name || '[Project Name]',
                role: it.role || '[Your Role]',
                duration: it.duration || '[Duration]',
                blurb: it.contribution || '[Contribution or Impact]',
              }),
            ),
            skills: _.get(props, 'location.state.payload.skillData.data') || [],
            languages: _.map(
              _.get(
                props,
                'location.state.payload.extraSectionData.languages',
              ) || [],
              (it: LanguageData) =>
                `${it.language || '[Language]'} (${
                  it.proficient || '[Proficiency]'
                })`,
            ),
            education:
              _.get(props, 'location.state.payload.educationData.data', [])
                .length > 0
                ? _.map(
                    _.get(
                      props,
                      'location.state.payload.educationData.data',
                      [],
                    ),
                    (d) => ({
                      school: _.get(d, 'universityName', 'Your University'),
                      degree: _.get(d, 'degree', 'Unknown degree'),
                      major: _.get(d, 'major', 'Your Major'),
                      start: moment(
                        _.get(d, 'startDate', '01/08/2015'),
                        'DD/MM/YYYY',
                      ).format('MMM YYYY'),
                      end:
                        _.get(d, 'endDate') &&
                        moment().format('DD/MM/YYYY') !== _.get(d, 'endDate')
                          ? moment(_.get(d, 'endDate'), 'DD/MM/YYYY').format(
                              'MMM YYYY',
                            )
                          : 'Now',
                      location: _.get(d, 'location', 'Indonesia'),
                      achievement: util.parseHtmlBulletAsString(
                        _.get(d, 'achievements') ||
                          t(constant.translation.cvMaker.formLoremIpsumShorter),
                      ),
                    }),
                  )
                : [
                    {
                      school: 'Your University',
                      major: 'Your Major',
                      degree: 'Unknown degree',
                      start: 'Aug 2015',
                      end: 'Now',
                      location: 'Indonesia',
                      achievement:
                        t(constant.translation.cvMaker.formLoremIpsumShorter) ||
                        '',
                    },
                  ],
          }}
        />
      ),
      downloadableDoc: (
        <ResumeTemplate.Onyx
          isPreview={false}
          data={{
            name: _.startCase(
              _.get(
                props,
                'location.state.payload.personalData.fullName',
                'Your Name',
              ),
            ),
            tagline: _.startCase(
              _.get(
                props,
                'location.state.payload.personalData.jobTitle',
                'Your current role',
              ),
            ),
            contacts: [
              {
                text: `${_.get(
                  props,
                  'location.state.payload.personalData.city',
                  'Your city',
                )}, ${_.get(
                  props,
                  'location.state.payload.personalData.country',
                  'Your country',
                )}`,
              },
              {
                text: _.get(
                  props,
                  'location.state.payload.personalData.email',
                  'your_email@gmail.com',
                ),
                href: `mailto:${_.get(
                  props,
                  'location.state.payload.personalData.email',
                  'your_email@gmail.com',
                )}`,
              },
              {
                text: `${_.get(
                  props,
                  'location.state.payload.personalData.phoneRegion',
                  '+62',
                )} ${_.get(
                  props,
                  'location.state.payload.personalData.phoneNumber',
                  '811-1111-1111',
                )}`,
              },
              {
                text: String(
                  _.get(
                    props,
                    'location.state.payload.personalData.linkedInProfile',
                    'linkedin.com/in/john-doe',
                  ),
                )
                  .replace(/^https?:\/\//, '')
                  .replace(/^www\./, ''),
                href: _.get(
                  props,
                  'location.state.payload.personalData.linkedInProfile',
                  'https://linkedin.com/in/john-doe',
                ),
              },
            ],
            summary:
              util.parseHtmlBulletAsString(
                _.get(
                  props,
                  'location.state.payload.professionalData.summary',
                ) || '',
              ) || t(constant.translation.cvMaker.formSummaryDefault),
            experience:
              _.get(props, 'location.state.payload.experienceData.data', [])
                .length > 0
                ? _.map(
                    _.get(
                      props,
                      'location.state.payload.experienceData.data',
                      [],
                    ),
                    (d) => ({
                      company: _.startCase(
                        _.get(d, 'employer', 'Your company'),
                      ),
                      role: _.startCase(_.get(d, 'jobTitle', 'Your role')),
                      start: moment(
                        _.get(d, 'startDate', '01/08/2015'),
                        'DD/MM/YYYY',
                      ).format('MMM YYYY'),
                      end:
                        _.get(d, 'endDate') &&
                        moment().format('DD/MM/YYYY') !== _.get(d, 'endDate')
                          ? moment(_.get(d, 'endDate'), 'DD/MM/YYYY').format(
                              'MMM YYYY',
                            )
                          : 'Now',
                      jobType: _.get(d, 'jobType', 'Full Time'),
                      location: _.get(d, 'location', 'Unknown Location'),
                      bullets: util.parseHtmlBullet(
                        _.get(d, 'jobDesc') ||
                          t(constant.translation.cvMaker.formLoremIpsumShorter),
                      ),
                    }),
                  )
                : [
                    {
                      company: 'Your company',
                      role: 'Your role',
                      start: 'Jan 1997',
                      end: 'Now',
                      jobType: 'Full Time',
                      location: 'Indonesia',
                      bullets: [
                        t(constant.translation.cvMaker.formLoremIpsumShorter),
                        t(constant.translation.cvMaker.formLoremIpsumShorter),
                      ],
                    },
                  ],
            projects: _.map(
              _.get(
                props,
                'location.state.payload.extraSectionData.projects',
              ) || [],
              (it: ProjectData) => ({
                name: it.name || '[Project Name]',
                role: it.role || '[Your Role]',
                year: it.year || '[Year]',
                blurb: it.contribution || '[Contribution or Impact]',
              }),
            ),
            certifications: _.map(
              _.get(
                props,
                'location.state.payload.extraSectionData.certifications',
              ) || [],
              (it: CertificationData) => ({
                title: it.name || '[Certificate Name]',
                org: it.issuer || '[Issuer]',
                year: it.year || '[Year]',
              }),
            ),
            volunteers: _.map(
              _.get(
                props,
                'location.state.payload.extraSectionData.volunteers',
              ) || [],
              (it: VolunteerData) => ({
                name: it.name || '[Project Name]',
                role: it.role || '[Your Role]',
                duration: it.duration || '[Duration]',
                blurb: it.contribution || '[Contribution or Impact]',
              }),
            ),
            skills: _.get(props, 'location.state.payload.skillData.data') || [],
            languages: _.map(
              _.get(
                props,
                'location.state.payload.extraSectionData.languages',
              ) || [],
              (it: LanguageData) =>
                `${it.language || '[Language]'} (${
                  it.proficient || '[Proficiency]'
                })`,
            ),
            education:
              _.get(props, 'location.state.payload.educationData.data', [])
                .length > 0
                ? _.map(
                    _.get(
                      props,
                      'location.state.payload.educationData.data',
                      [],
                    ),
                    (d) => ({
                      school: _.get(d, 'universityName', 'Your University'),
                      degree: _.get(d, 'degree', 'Unknown degree'),
                      major: _.get(d, 'major', 'Your Major'),
                      start: moment(
                        _.get(d, 'startDate', '01/08/2015'),
                        'DD/MM/YYYY',
                      ).format('MMM YYYY'),
                      end:
                        _.get(d, 'endDate') &&
                        moment().format('DD/MM/YYYY') !== _.get(d, 'endDate')
                          ? moment(_.get(d, 'endDate'), 'DD/MM/YYYY').format(
                              'MMM YYYY',
                            )
                          : 'Now',
                      location: _.get(d, 'location', 'Indonesia'),
                      achievement: util.parseHtmlBulletAsString(
                        _.get(d, 'achievements') ||
                          t(constant.translation.cvMaker.formLoremIpsumShorter),
                      ),
                    }),
                  )
                : [
                    {
                      school: 'Your University',
                      major: 'Your Major',
                      degree: 'Unknown degree',
                      start: 'Aug 2015',
                      end: 'Now',
                      location: 'Indonesia',
                      achievement:
                        t(constant.translation.cvMaker.formLoremIpsumShorter) ||
                        '',
                    },
                  ],
          }}
        />
      ),
    });

    if (location?.state !== null) {
      this.setState({
        cvName: _.get(props, 'location.state.payload.name'),
        previousScreen: _.get(props, 'location.state.from'),
      });
    } else {
      // Send to previous page if there's no CV data
      window.history.back();
    }

    await Promise.allSettled([
      new Promise((resolve, reject) => {
        try {
          this.getDefaultPackage();
          resolve();
        } catch (e) {
          reject(e);
        }
      }),

      new Promise((resolve, reject) => {
        try {
          this.getPaymentMethods();
          resolve();
        } catch (e) {
          reject(e);
        }
      }),
    ]);
  }

  componentDidUpdate(): void {
    const { showSuccessModal } = this.state;
    const { location } = this.props;

    if (showSuccessModal) {
      EventTrackerAPI.pushEvent(location.pathname, 'download-cv');
    }
  }

  componentWillUnmount(): void {
    this.debouncedApplyCode.cancel();
  }

  private getDefaultPackage = async (): Promise<void> => {
    try {
      const response = await OrderAPI.getDefaultPackage();

      const payload: any = _.get(this, 'state.payload');
      const packageId: number = _.get(response, 'data.id') || 0;
      const price = _.get(response, 'data.price') || 0;

      this.setState({
        payload: {
          ...payload,
          packageId,
        },
        price,
      });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  private getPaymentMethods = async (): Promise<void> => {
    try {
      const response = await MiscAPI.getPaymentMethods();
      const data = _.get(response, 'data') || [];
      this.setState({ paymentMethods: data });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  private applyCode = async (
    paymentMethod: any = '',
    showModal = true,
  ): Promise<void> => {
    try {
      const { payload } = this.state;
      if (!payload.packageId) {
        return;
      }

      const response = await OrderAPI.calculateOrder(
        payload.packageId || '',
        payload.coupon || '',
        paymentMethod || payload.paymentMethod || '',
      );
      this.setState({
        adminFee: _.get(response, 'data.adminFee') || 0,
        price: _.get(response, 'data.slashPrice') || 0,
        discounted: _.get(response, 'data.discount') || 0,
        discountedPrice: _.get(response, 'data.discountedPrice') || 0,
      });

      if (payload.coupon && showModal) {
        toaster.push(
          util.buildMessage(
            `Successfully applied the promo code: ${payload.coupon}`,
          ),
        );
      }
    } catch (e: any) {
      this.setState({
        discounted: 0,
        discountedPrice: _.get(this, 'state.price') || 0,
      });
      toaster.push(
        util.buildErrorMessage(_.get(e, 'response.data.message') || e.message),
      );
    }
  };

  private copyToClipboard = async (prop: string): Promise<void> => {
    try {
      const { state } = this;
      await navigator.clipboard.writeText(String(_.get(state, prop)));

      toaster.push(util.buildInfoMessage('Copied to clipboard!'));
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  private refreshStatus = async (): Promise<void> => {
    try {
      this.setState({ loadingRefresh: true });

      const { invoiceNumber } = this.state;
      const resp = await InvoiceAPI.getInvoiceByNumber(invoiceNumber || '');
      const status = _.get(resp, 'data.data.status');

      if (status === 'success') {
        const filename = _.get(resp, 'data.data.file.filename');

        this.setState({
          showModal: false,
          showSuccessModal: true,
          downloadableFilename: filename,
        });
      } else if (status !== 'pending') {
        this.setState({
          showModal: false,
          showFailedModal: true,
        });
      }
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    } finally {
      this.setState({ loadingRefresh: false });
    }
  };

  private downloadCV = async (): Promise<void> => {
    try {
      const { downloadableFilename: filename } = this.state;
      await DocumentAPI.downloadFile(filename || '');

      const { navigate } = this.props;
      navigate('/dashboard/candidate/my-cv-maker');
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    } finally {
      this.setState({ showSuccessModal: false });
    }
  };

  private renderModal = ({ t } = this.props): React.ReactNode | null => (
    <Modal
      size="md"
      className="cv-maker-payment-modal"
      open={_.get(this.state, 'showModal')}
      backdrop="static"
    >
      <Modal.Body className="!px-3 !py-2">
        <Stack direction="column" spacing={24}>
          <Stack.Item>
            <img src="/icon-cvmaker-pending-payment.png" alt="logo-pending" />
          </Stack.Item>
          <Stack.Item>
            <HeadingGroup className="cvmaker-title-header">
              <Heading>
                {t(constant.translation.cvMaker.pendingPaymentTitle)}
              </Heading>
              <Text muted>
                {t(constant.translation.cvMaker.pendingPaymentSubTitle)}
              </Text>
            </HeadingGroup>
          </Stack.Item>
          <Stack.Item>
            <Button
              className="btn-expire"
              appearance="ghost"
              startIcon={<FaClock />}
            >
              {'Expire in: '}
              <b style={{ marginLeft: '.5em' }}>
                {_.get(this.state, 'expiresIn', 0) > 0
                  ? util.formatTimeWithHour(_.get(this.state, 'expiresIn') || 0)
                  : 'EXPIRED'}
              </b>
            </Button>
          </Stack.Item>
          <Stack.Item>
            <Divider />
          </Stack.Item>
          <Stack.Item className="w-100">
            <Stack
              direction="row"
              justifyContent="center"
              className={`mb-4 ${
                _.get(this.state, 'qrUrl') ? 'd-block' : 'd-none'
              }`}
            >
              <Stack.Item>
                <Stack
                  className="payment-details"
                  direction="column"
                  alignItems="flex-start"
                >
                  <img
                    src={_.get(this.state, 'qrUrl')}
                    alt="qris"
                    style={{ maxWidth: '25rem' }}
                  />
                </Stack>
              </Stack.Item>
            </Stack>
            <Stack
              direction="row"
              justifyContent={
                _.get(this.state, 'vaNumber') ? 'space-between' : 'center'
              }
            >
              <Stack.Item
                style={{
                  display: _.get(this.state, 'vaNumber') ? 'block' : 'none',
                }}
              >
                <Stack
                  className="payment-details"
                  direction="column"
                  alignItems="flex-start"
                >
                  <p>
                    {_.get(this.state, 'payload.paymentMethod') === 'MANDIRI'
                      ? 'Bill Payment'
                      : 'VA Number'}
                    {_.get(this.state, 'payload.paymentMethod')
                      ? ` - ${_.get(this.state, 'payload.paymentMethod')}`
                      : null}
                  </p>
                  <p className="subtitle">
                    {_.get(this.state, 'vaNumber')}{' '}
                    <IconButton
                      icon={<FaCopy />}
                      onClick={() => this.copyToClipboard('vaNumber')}
                    />
                  </p>
                </Stack>
              </Stack.Item>
              <Stack.Item
                style={{
                  display: _.get(this.state, 'billerCode') ? 'block' : 'none',
                }}
              >
                <Stack
                  className="payment-details"
                  direction="column"
                  alignItems="flex-start"
                >
                  <p>Biller Code</p>
                  <p className="subtitle">
                    {_.get(this.state, 'billerCode')}{' '}
                    <IconButton
                      icon={<FaCopy />}
                      onClick={() => this.copyToClipboard('billerCode')}
                    />
                  </p>
                </Stack>
              </Stack.Item>
              <Stack.Item>
                <Stack
                  className="payment-details"
                  direction="column"
                  alignItems="flex-start"
                >
                  <p>Total Payment</p>
                  <p className="subtitle">
                    {util.formatCurrency(_.get(this.state, 'discountedPrice'))}{' '}
                    <IconButton
                      icon={<FaCopy />}
                      onClick={() => this.copyToClipboard('discountedPrice')}
                    />
                  </p>
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
          <Stack.Item>
            <Divider />
          </Stack.Item>
          <Stack.Item>
            <div className="note-container">
              <p>
                - Once your payment is confirmed, we'll email your CV and
                invoice to you.
              </p>
              <p>
                - If you encounter any issues, feel free to reach out to us at{' '}
                <b>halotalentgrowth@gmail.com</b>. We're happy to help!
              </p>
            </div>
          </Stack.Item>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Stack
          spacing={16}
          className="w-100"
          justifyContent="flex-end"
          direction="row"
        >
          {/* <Stack.Item>
            <Button
              appearance="subtle"
              className="btn-tg-fortier"
              onClick={() => this.setState({ showModal: false })}
            >
              {t(constant.translation.general.formCancelButtonKey)}
            </Button>
          </Stack.Item> */}
          <Stack.Item>
            <Button
              className="next-btn"
              appearance="primary"
              disabled={_.get(this.state, 'loadingRefresh')}
              onClick={this.refreshStatus}
              startIcon={<ReloadIcon />}
            >
              {_.get(this.state, 'loadingRefresh')
                ? 'Checking...'
                : t(constant.translation.general.formRefreshStatButtonKey)}
            </Button>
          </Stack.Item>
        </Stack>
      </Modal.Footer>
    </Modal>
  );

  private renderSuccessModal = (
    { t, navigate } = this.props,
  ): React.ReactNode | null => (
    <Modal
      className="cv-maker-payment-modal"
      open={_.get(this.state, 'showSuccessModal')}
      onClose={() => {
        this.setState({ showSuccessModal: false });
        navigate('/dashboard/candidate/my-cv-maker');
      }}
    >
      <Modal.Body className="!px-3 !py-2">
        <Stack direction="column" spacing={24}>
          <Stack.Item>
            <img src="/icon-cvmaker-success-payment.png" alt="logo-success" />
          </Stack.Item>
          <Stack.Item>
            <HeadingGroup className="cvmaker-title-header">
              <Heading>
                {t(constant.translation.cvMaker.successPaymentTitle)}
              </Heading>
              <Text muted>
                {t(constant.translation.cvMaker.successPaymentSubTitle)}
              </Text>
              <Text muted>
                {t(constant.translation.cvMaker.paymentHelpTitle)}
              </Text>
            </HeadingGroup>
          </Stack.Item>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Stack
          spacing={16}
          className="w-100"
          justifyContent="flex-end"
          direction="row"
        >
          <Stack.Item>
            <Button
              className="next-btn"
              appearance="primary"
              onClick={this.downloadCV}
              startIcon={<Icon as={FaCloudDownloadAlt} />}
            >
              {t(constant.translation.general.formDownloadCVButtonKey)}
            </Button>
          </Stack.Item>
        </Stack>
      </Modal.Footer>
    </Modal>
  );

  private renderFailedModal = ({ t } = this.props): React.ReactNode | null => (
    <Modal
      className="cv-maker-payment-modal"
      open={_.get(this.state, 'showFailedModal')}
      backdrop="static"
    >
      <Modal.Body className="!px-3 !py-2">
        <Stack direction="column" spacing={24}>
          <Stack.Item>
            <img src="/icon-cvmaker-failed-payment.png" alt="logo-success" />
          </Stack.Item>
          <Stack.Item>
            <HeadingGroup className="cvmaker-title-header">
              <Heading>
                {t(constant.translation.cvMaker.failedPaymentTitle)}
              </Heading>
              <Text muted>
                {t(constant.translation.cvMaker.failedPaymentSubTitle)}
              </Text>
              <Text muted>
                {t(constant.translation.cvMaker.paymentHelpTitle)}
              </Text>
            </HeadingGroup>
          </Stack.Item>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Stack
          spacing={16}
          className="w-100"
          justifyContent="flex-end"
          direction="row"
        >
          <Stack.Item>
            <Button
              className="next-btn"
              appearance="primary"
              onClick={this.onPayButtonClicked}
              startIcon={<ReloadIcon />}
            >
              {t(constant.translation.general.formRetryPaymentButtonKey)}
            </Button>
          </Stack.Item>
        </Stack>
      </Modal.Footer>
    </Modal>
  );

  private onFormValueChanged = (value: any, key: string): void =>
    this.setState((prevState) => util.setNested(prevState, key, value));

  private onPayButtonClicked = async (): Promise<void> => {
    const { t } = this.props;

    try {
      this.setState({ payButtonEnabled: false });

      if (!_.invoke(this.formPaymentRef, 'current.check')) {
        throw new Error(
          t(constant.translation.basicInfo.formErrorValidationMessageKey),
        );
      }

      this.setState({ loading: true });
      window.scrollTo(0, 0);

      // Place an order
      const { payload, cvName } = this.state;
      const resp = await OrderAPI.placeOrder(payload);

      const status = _.get(resp, 'data.status');
      const invoiceNumber = _.get(resp, 'data.number', '');

      let showUrl = false;
      if (status === 'success') {
        showUrl = true;
      }

      // Upload file pdf to document api
      const { downloadableDoc } = this.state;
      const blob = await pdf(downloadableDoc).toBlob();
      const base64 = await util.blobToBase64(blob);
      const r = await DocumentAPI.upload({
        content: base64,
        showUrl,
        table: 'App\\Models\\Invoice',
        tableId: _.get(resp, 'data.id'),
        filename: cvName,
      });

      // Send invoice to targeted email
      if (invoiceNumber && showUrl) {
        await InvoiceAPI.sendInvoiceToEmail(invoiceNumber, payload.email);
      }

      const { discounted, price } = this.state;
      const p = price || 0;
      if (discounted === p) {
        const filename = _.get(r, 'data.filename');

        this.setState({
          showSuccessModal: true,
          downloadableFilename: filename,
        });
      } else {
        const expired = new Date(
          String(_.get(resp, 'data.expiresIn')),
        ).getTime();

        setInterval(() => {
          const now = new Date().getTime();
          this.setState({ expiresIn: (expired - now) / 1000 });
        }, 1000);

        const now = new Date().getTime();
        this.setState({
          showModal: true,
          invoiceNumber: _.get(resp, 'data.number'),
          vaNumber: _.get(resp, 'data.vaNumber'),
          billerCode: _.get(resp, 'data.billerCode'),
          qrUrl: _.get(resp, 'data.qrUrl'),
          discountedPrice: _.get(resp, 'data.price'),
          expiresIn: (expired - now) / 1000,
        });
      }

      SessionStorage.removeState();
    } catch (e: any) {
      toaster.push(
        util.buildErrorMessage(_.get(e, 'response.data.message') || e.message),
      );
    } finally {
      this.setState({
        payButtonEnabled: true,
        loading: false,
      });
    }
  };

  private renderForms = (): React.ReactNode | null => {
    const storage = AuthStorage.getInstance();
    const user = storage.getUser();
    const { price, discountedPrice, discounted, adminFee } = this.state;
    const { t } = this.props;

    return (
      <Form
        key="payment-form"
        ref={this.formPaymentRef}
        model={this.formPaymentModel}
        formValue={_.get(this.state, 'payload')}
        fluid
        onChange={(v: object) => this.onFormValueChanged(v, 'payload')}
      >
        <FlexboxGrid justify="start" align="top">
          <FlexboxGrid.Item as={Col} xs={24}>
            <HeadingGroup className="cvmaker-title-header">
              <Heading>{t(constant.translation.cvMaker.paymentTitle)}</Heading>
              <Text muted>
                {t(constant.translation.cvMaker.paymentSubTitle)}
              </Text>
            </HeadingGroup>
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} xs={24}>
            <Divider />
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} xs={24}>
            <Stack
              justifyContent="space-between"
              className={PAYMENT_FORM_SECTION_CLASS}
            >
              <Stack.Item>
                <HeadingGroup className="cvmaker-title-header secondary max-md:!w-full">
                  <Heading>
                    {t(constant.translation.cvMaker.contactTitle)}
                  </Heading>
                  <Text muted>
                    {t(constant.translation.cvMaker.contactLabel)}
                  </Text>
                </HeadingGroup>
              </Stack.Item>
              <Stack.Item>
                <Form.Control
                  accepter={RadioGroup}
                  name="email"
                  className="max-md:w-full max-md:min-w-0"
                  defaultValue={_.get(this.state, 'payload.email')}
                  value={_.get(this.state, 'payload.email')}
                >
                  <Radio value={_.get(user, 'email')}>
                    <Stack
                      className="email-label"
                      direction="column"
                      alignItems="flex-start"
                    >
                      <Stack.Item>Your registered email</Stack.Item>
                      <Stack.Item className="max-md:break-all">
                        {_.get(user, 'email', '-')}
                      </Stack.Item>
                    </Stack>
                  </Radio>
                  <Radio
                    ref={this.otherEmailRadioRef}
                    value={_.get(this.state, 'otherEmail', '-')}
                  >
                    <Stack
                      direction="column"
                      alignItems="flex-start"
                      className="max-md:w-full max-md:min-w-0"
                    >
                      <Stack.Item>Send to other email</Stack.Item>
                      <Stack.Item className="max-md:w-full max-md:min-w-0">
                        <Input
                          name="otherEmail"
                          onChange={(v: string) => {
                            this.onFormValueChanged(v, 'payload.email');
                            this.setState({ otherEmail: v });

                            setTimeout(() => {
                              _.invoke(this.formPaymentRef, 'current.check');
                            }, 500);
                          }}
                          className="mt-2 max-md:w-full max-md:min-w-0"
                          placeholder="Enter your email here"
                        />
                      </Stack.Item>
                    </Stack>
                  </Radio>
                </Form.Control>
              </Stack.Item>
            </Stack>
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} xs={24}>
            <Divider />
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} xs={24}>
            <Stack
              justifyContent="space-between"
              className={PAYMENT_FORM_SECTION_CLASS}
            >
              <Stack.Item>
                <HeadingGroup className="cvmaker-title-header secondary max-md:!w-full">
                  <Heading>
                    {t(constant.translation.cvMaker.paymentMethodTitle)}
                  </Heading>
                  <Text muted>
                    {t(constant.translation.cvMaker.paymentMethodLabel)}
                  </Text>
                </HeadingGroup>
              </Stack.Item>
              <Stack.Item>
                <Form.Control
                  accepter={RadioGroup}
                  name="paymentMethod"
                  className="max-md:w-full max-md:min-w-0"
                  defaultValue={_.get(this.state, 'payload.paymentMethod')}
                  value={_.get(this.state, 'payload.paymentMethod')}
                  onChange={(val) => this.applyCode(val, false)}
                >
                  <Stack
                    direction="column"
                    spacing={16}
                    alignItems="flex-start"
                  >
                    {(_.get(this.state, 'paymentMethods') || []).map((pm) => (
                      <Stack.Item className="w-100">
                        <Panel
                          bordered
                          className="cvmaker-paymentmethod-panel"
                          bodyProps={{ className: 'max-md:!px-3' }}
                        >
                          <Radio value={_.get(pm, 'code')}>
                            <Stack spacing={8}>
                              <Stack.Item>
                                <img
                                  src={_.get(pm, 'logo')}
                                  alt={_.get(pm, 'code')}
                                />
                              </Stack.Item>
                              <Stack.Item className="max-md:min-w-0 max-md:break-words">
                                {_.get(pm, 'name')}
                              </Stack.Item>
                            </Stack>
                          </Radio>
                        </Panel>
                      </Stack.Item>
                    ))}
                  </Stack>
                </Form.Control>
              </Stack.Item>
            </Stack>
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} xs={24}>
            <Divider />
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} xs={24}>
            <Stack
              justifyContent="space-between"
              className={PAYMENT_FORM_SECTION_CLASS}
            >
              <Stack.Item>
                <HeadingGroup className="cvmaker-title-header secondary max-md:!w-full">
                  <Heading>
                    {t(constant.translation.cvMaker.couponCodeTitle)}
                  </Heading>
                  <Text muted>
                    {t(constant.translation.cvMaker.couponCodeLabel)}
                  </Text>
                </HeadingGroup>
              </Stack.Item>
              <Stack.Item>
                <Stack
                  direction="column"
                  alignItems="flex-start"
                  className="max-md:w-full max-md:min-w-0"
                >
                  <Stack.Item className="max-md:w-full max-md:min-w-0">
                    <InputGroup className="max-md:min-w-0 max-md:[&_.rs-form-control-wrapper]:min-w-0 max-md:[&_.rs-input]:min-w-0">
                      <InputGroup.Addon>
                        <FaGift />
                      </InputGroup.Addon>
                      <Form.Control
                        accepter={Input}
                        name="coupon"
                        className="max-md:!w-full max-md:min-w-0"
                        defaultValue={_.get(this.state, 'payload.coupon')}
                        value={_.get(this.state, 'payload.coupon')}
                        onChange={this.debouncedApplyCode}
                      />
                      <InputGroup.Button onClick={() => this.applyCode()}>
                        Apply Code
                      </InputGroup.Button>
                    </InputGroup>
                  </Stack.Item>
                </Stack>
              </Stack.Item>
            </Stack>
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} xs={24} className="mt-8">
            <Stack
              justifyContent="space-between"
              className={PAYMENT_FORM_SECTION_CLASS}
            >
              <Stack.Item grow={1}>
                <HeadingGroup className="cvmaker-title-header tertiary max-md:!w-full">
                  <Heading>Total{adminFee > 0 ? ' + Admin Fee' : ''}</Heading>
                  <Stack
                    direction="row"
                    spacing={12}
                    className="max-md:flex-wrap max-md:!gap-2"
                  >
                    <Text
                      className={
                        (discounted || 0) > 0 ? 'slash-price' : 'unslash-price'
                      }
                      {...((discounted || 0) > 0 ? { muted: true } : {})}
                    >
                      {util.formatCurrency((price || 0) + adminFee, 'IDR ')}
                    </Text>
                    {(discounted || 0) > 0 && (
                      <Text className="actual-price">
                        {util.formatCurrency(
                          (discountedPrice || 0) + adminFee,
                          'IDR ',
                        )}
                      </Text>
                    )}
                  </Stack>
                </HeadingGroup>
              </Stack.Item>
              <Stack.Item>
                <Button
                  className="next-btn max-md:!w-full"
                  appearance="primary"
                  size="lg"
                  onClick={this.onPayButtonClicked}
                  disabled={!_.get(this.state, 'payButtonEnabled')}
                >
                  {t(constant.translation.general.formPayNowButtonKey)}
                </Button>
              </Stack.Item>
            </Stack>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </Form>
    );
  };

  private renderCVPreview = (): React.ReactNode | null => {
    const { doc } = this.state;

    return (
      <PDFViewer
        showToolbar={false}
        width="100%"
        height="100%"
        className="h-80vh"
      >
        {doc}
      </PDFViewer>
    );
  };

  render(): React.ReactNode | null {
    const { step, previousScreen, loading } = this.state;
    const { t, navigate } = this.props;

    return (
      <div className="cv-maker-payment-steps-container max-md:!w-[calc(100%-32px)]">
        {this.renderModal()}
        {this.renderSuccessModal()}
        {this.renderFailedModal()}
        <FlexboxGrid
          justify="start"
          align="top"
          className="max-md:!grid max-md:grid-cols-1"
        >
          <FlexboxGrid.Item as={Col} xs={24} className="mb-4">
            <a
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (window.history.length > 1) navigate(-1);
                else {
                  navigate(previousScreen);
                }
              }}
              className="btn-tg-back"
            >
              <FaArrowLeft />
              <span>
                {t(constant.translation.general.formBackEditCVButtonKey)}
              </span>
            </a>
          </FlexboxGrid.Item>
          <FlexboxGrid.Item
            as={Col}
            xs={10}
            className="max-md:!w-full max-md:min-w-0"
          >
            {this.renderCVPreview()}
          </FlexboxGrid.Item>
          <FlexboxGrid.Item
            as={Col}
            xs={14}
            className="max-md:!w-full max-md:min-w-0 max-md:mt-4"
          >
            <Steps
              current={step}
              className="ml-4 cv-maker-payment max-md:!ml-0"
            >
              <Steps.Item
                title="Payment"
                description="Secure & Smooth"
                icon={<FaShareAlt />}
              />
              <Steps.Item
                title="Download Your CV"
                description="Start applying!"
                icon={<FaIdCard />}
              />
            </Steps>
            <Panel
              className="cv-maker-steps-panel w-100"
              bodyProps={{ className: 'max-md:!px-3' }}
            >
              {this.renderForms()}
            </Panel>
          </FlexboxGrid.Item>
        </FlexboxGrid>

        {loading && (
          <Loader
            backdrop
            content={t(constant.translation.general.formLoadingTextKey)}
            vertical
          />
        )}
      </div>
    );
  }
}

export default withRouter(withTranslation()(CVMakerPaymentScreen));
